import { useCallback, useRef, useState } from "react";
import { ITabulator, ITabulatorProps } from "./types";
import { useNavigate } from "react-router-dom";
import {
	AccessorModule,
	AjaxConfig,
	AjaxModule,
	ColumnCalcsModule,
	ColumnDefinition,
	DownloadModule,
	ExportModule,
	FormatModule,
	InteractionModule,
	PageModule,
	ResizeColumnsModule,
	ResponsiveLayoutModule,
	SelectRowModule,
	SortModule,
	Tabulator,
	TooltipModule,
} from "tabulator-tables"; //import Tabulator library
import { AjaxObj, ajaxLoader } from "./ajaxLoader";
import { PerfectScrollbarWrapper } from "../PerfectScrollbar/perfectScrollbarWrapper";
import { SpinnerWrapper } from "../Spinner/spinnerWrapper";
import { tabulatorInstance } from "./tabulatorInstance";
import { ITabulatorOptions, TabulatorCell } from "./types";
import { Type } from "../../../data/types/Type";

Tabulator.registerModule([
	AccessorModule,
	AjaxModule,
	ColumnCalcsModule,
	//DataTreeModule,
	DownloadModule,
	ExportModule,
	//FilterModule,
	FormatModule,
	//FrozenColumnsModule,
	InteractionModule,
	TooltipModule,
	PageModule,
	ResizeColumnsModule,
	ResponsiveLayoutModule,
	SelectRowModule,
	SortModule,
]);

type OnCellClickProp = ((e: UIEvent, cell: TabulatorCell) => void) | undefined;

let tabulatorIdCounter = 0;

const debugLog = (id: string | undefined, ...args: unknown[]) => {
	//console.debug(`${colors.green}Tabulator ${id}${colors.reset}: `, ...args);
};

export function useTabulator(): ITabulator {
	const navigate = useNavigate();
	const perfectScrollbar = useRef(new PerfectScrollbarWrapper());
	const tabulatorId = useRef<string | undefined>(undefined);
	const ref = useRef<Tabulator | undefined>(undefined);
	const isReady = useRef<boolean>(false);
	const pendingColumns = useRef<ColumnDefinition[] | undefined>(undefined);
	const pendingData = useRef<{ data: unknown; params?: unknown } | undefined>(undefined);
	const pendingHeight = useRef<number | string | false | undefined>(undefined);
	const onCellClick = useRef<OnCellClickProp>(undefined);
	const onRowClick = useRef<any>(undefined);
	const onDataSorted = useRef<any>(undefined);
	const ajaxObj = useRef<AjaxObj>({
		abortController: new AbortController(),
		spinner: new SpinnerWrapper(),
	} as AjaxObj);
	const [ajaxUrl, setAjaxUrl] = useState<string | undefined>();

	const create = useCallback(
		(element: HTMLElement, props: ITabulatorProps): void => {
			if (ref.current || !element) {
				return;
			}
			isReady.current = false;
			const options = props.options;
			const data: unknown[] | undefined = Array.isArray(props.data) ? props.data : undefined;
			const url = Type.isString(props.data) ? props.data : undefined;

			tabulatorId.current = props.id || `tab-${++tabulatorIdCounter}`;
			element.id = tabulatorId.current;
			ajaxObj.current.element = element;
			ajaxObj.current.abortController = new AbortController();
			setAjaxUrl(url);

			const baseOptions: ITabulatorOptions = {
				...defaultOptions,
				...options,
				//rowClick: options.onRowClick,
				ajaxURL: url,
				data,
				...ajaxLoader(ajaxObj.current, options),
			};

			const openLinkOnCellClick = (event: UIEvent, cell: TabulatorCell) => {
				if (options.getCellLink && ref.current) {
					const link = options.getCellLink(cell);
					if (link) {
						navigate(link);
						event.stopPropagation();
					} else {
						if (options.onCellClick) {
							if (options.onCellClick(event, cell)) {
								return;
							}
						}
						event.stopPropagation();
					}
				}
			};

			const attachPerfectScrollbar = () => {
				const children = element.children;
				if (children && children.length > 1) {
					const tableHolder = element.children[1];
					if (tableHolder.className === "tabulator-tableholder") {
						perfectScrollbar.current.attach(tableHolder);
					}
				}
			};

			if (baseOptions.responseToData) {
				delete baseOptions.responseToData;
			}
			if (baseOptions.onLoadFinished) {
				delete baseOptions.onLoadFinished;
			}
			if (baseOptions.onDataSorted) {
				delete baseOptions.onDataSorted;
			}
			if (baseOptions.onRowClick) {
				delete baseOptions.onRowClick;
			}

			if (options.onCellClick) {
				onCellClick.current = options.onCellClick;
				delete baseOptions.onCellClick;
			}
			if (options.getCellLink) {
				onCellClick.current = openLinkOnCellClick as unknown as OnCellClickProp;
				delete baseOptions.getCellLink;
			}
			if (tabulatorInstance.onCreating) {
				tabulatorInstance.onCreating(baseOptions);
			}

			debugLog(tabulatorId.current, `Creating ajaxUrl(${ajaxUrl}) prop(${url})`);
			const tabRef = new Tabulator(element, baseOptions);
			ref.current = tabRef;

			tabRef.on("tableBuilt", () => {
				isReady.current = true;

				if (onCellClick.current) {
					tabRef.on("cellClick", onCellClick.current);
				}
				if (options.onDataSorted) {
					onDataSorted.current = options.onDataSorted;
					tabRef.on("dataSorted", options.onDataSorted);
				}
				if (options.onRowClick) {
					onRowClick.current = options.onRowClick;
					tabRef.on("rowClick", options.onRowClick);
				}
				attachPerfectScrollbar();
				ajaxObj.current.spinner.refresh(element);

				// Updates that arrived before "tableBuilt" are buffered and are now flushed when the table is ready.
				if (pendingColumns.current) {
					const columns = pendingColumns.current;
					pendingColumns.current = undefined;
					setColumns(columns);
				}
				if (pendingData.current) {
					const pending = pendingData.current;
					pendingData.current = undefined;
					setData(pending.data, pending.params);
				}
				if (pendingHeight.current !== undefined) {
					const height = pendingHeight.current;
					pendingHeight.current = undefined;
					setHeight(height);
				}

				tabRef.off("tableBuilt");
				debugLog(tabulatorId.current, `Created ajaxUrl(${ajaxUrl}) prop(${url})`);
			});
		},
		[ref, navigate, ajaxUrl],
	);

	const destroy = () => {
		const tabRef = ref.current;
		if (tabRef !== undefined) {
			debugLog(tabulatorId.current, `Destroying`);
			isReady.current = false;
			pendingColumns.current = undefined;
			pendingData.current = undefined;
			pendingHeight.current = undefined;
			ref.current = undefined;
			ajaxObj.current.abortController.abort();
			if (onCellClick.current) {
				tabRef.off("cellClick");
			}
			if (onDataSorted.current) {
				tabRef.off("dataSorted");
			}
			if (onRowClick.current) {
				tabRef.off("rowClick");
			}
			tabRef.clearData();
			tabRef.destroy();
			perfectScrollbar.current.detach();
			debugLog(tabulatorId.current, `Destroyed`);
		}
	};

	const setColumns = useCallback(
		(columns?: ColumnDefinition[]): void => {
			if (!columns) {
				return;
			}
			// If table is not built yet, buffer the update and apply it after tableBuilt.
			if (!ref.current || !isReady.current) {
				pendingColumns.current = columns;
				return;
			}

			if (ref.current && columns) {
				const oldColumns = ref.current.getColumns();
				const len = columns.length;

				if (oldColumns.length === len) {
					let i = 0;
					while (i < len && oldColumns[i].getField() === columns[i].field) {
						i++;
					}
					if (i === len) {
						return;
					}
				}
				debugLog(tabulatorId.current, `Set columns`);
				ref.current.setColumns(columns);
			}
		},
		[ref],
	);

	const setData = useCallback(
		(data: unknown, params?: unknown): void => {
			// If table is not built yet, buffer the update and apply it after tableBuilt.
			if (!ref.current || !isReady.current) {
				pendingData.current = { data, params };
				return;
			}

			const setUrl = (url: string, params: unknown) => {
				if (ref.current && (url !== ajaxUrl || params)) {
					setAjaxUrl(url);
					debugLog(tabulatorId.current, `setUrl(${data})`);
					ajaxObj.current.spinner.hide();
					ajaxObj.current.abortController = new AbortController();
					ref.current.setData(url, params);
				}
			};
			const setArray = (array: unknown[]) => {
				if (ref.current) {
					debugLog(tabulatorId.current, `Set data array[${array.length}] row(s)`);
					setAjaxUrl(undefined);
					ref.current.setData(array);
				}
			};

			const clearData = () => {
				setAjaxUrl(undefined);
				if (ref.current && ref.current.getDataCount()) {
					ref.current.clearData();
					debugLog(tabulatorId.current, `clearData`);
				}
			};

			if (!data) {
				clearData();
			} else if (Type.isString(data)) {
				if (ref.current) {
					setUrl(data, params);
				}/* else {
					setTimeout(() => {
						setUrl(data, params);
					}, 300);
				}*/
			} else if (Array.isArray(data)) {
				setArray(data);
			}
		},
		[ref, ajaxUrl],
	);

	/*
	const setFilter = useCallback(
		(p1: TabulatorFilterParam, p2?: FilterType | {}, value?: any, filterParams?: FilterParams): void => {
			if (ref.current) {
				debugLog(tabulatorId.current, `Set filter`);
				if (p1 && p1.length > 0) {
					ref.current.setFilter(p1, p2, value, filterParams);
				} else {
					ref.current.clearFilter(true);
				}
			}
		},
		[ref],
	);*/

	const setHeight = useCallback(
		(height: number | string | false | undefined) => {
			// If table is not built yet, buffer the update and apply it after tableBuilt.
			if (!ref.current || !isReady.current) {
				pendingHeight.current = height;
				return;
			}

			if (ref.current && height !== undefined && height !== false) {
				debugLog(tabulatorId.current, `SetHeight(${height})`);
				ref.current.setHeight(height);
			}
		},
		[ref],
	);

	const download = useCallback((downloadType: any, fileName: string, params?: any, filter?: any): void => {
		if (ref.current) {
			ref.current.download(downloadType, fileName, params, filter);
		}
	}, [ref]);

	return {
		create,
		setColumns,
		setData,
		//setFilter,
		setHeight,
		download,
		destroy,
	};
}

const defaultOptions: ITabulatorOptions = {
	selectableRows: "highlight",
	responsiveLayout: "collapse",
	height: 450,
	layout: "fitColumns",
	placeholder: tabulatorInstance.defaults.placeholder,
	progressiveLoadScrollMargin: 200,
	ajaxConfig: tabulatorInstance.defaults.ajaxConfig as AjaxConfig,
	dataLoader: true,
	dataSendParams: tabulatorInstance.defaults.dataSendParams,
	dataReceiveParams: tabulatorInstance.defaults.dataReceiveParams,
	dataLoaderLoading: tabulatorInstance.defaults.loadingMessage,
	dataLoaderError: tabulatorInstance.defaults.errorMessage,
};
